import request from "@libs/request/request";
import useWindowSize from "src/hooks/useWindowSize";
import OfferItem from "./OfferItem";
import FilterBoxes from "./Filters/FilterBoxes";
import SearchBar from "@components/NewSearchBar/SearchPage/SearchBar";
import { useRouter } from "next/router";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoClose, IoMapOutline } from "react-icons/io5";
import { RxMixerHorizontal } from "react-icons/rx";
import { Button, Skeleton, List, Empty, Image, Modal, Drawer } from "antd";
import { NextSeo } from "next-seo";
import dynamic from "next/dynamic";

const FavoriteOffersLoader = dynamic(
  () => import("@libs/helper/FavoriteOffersLoader"),
  {
    ssr: false,
  }
);
const GoogleMapModal = dynamic(() => import("./GoogleMapModal"), {
  ssr: false,
});
const Adds = dynamic(() => import("components/search/adds"), {
  ssr: false,
});
const Newsletter = dynamic(() => import("@components/Newsletter/Newsletter"), {
  ssr: false,
});
const HomeSectioncomminity = dynamic(
  () => import("@components/frontpage/main_section_comminity"),
  {
    ssr: false,
  }
);

const NewSearchSystem = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [isFiltersLoading, setIsFiltersLoading] = useState(true);
  const [filtersData, setFiltersData] = useState({});
  const [hasFailed, setHasFailed] = useState(false);
  const [isFiltersLoadingInline, setIsFiltersLoadingInline] = useState(false);
  const [hasFilterReqFailed, setHasFilterReqFailed] = useState(false);
  const [mainTitle, setMainTitle] = useState(null);
  const [openMap, setOpenMap] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [loadSearchBar, setLoadSearchBar] = useState(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [paginationData, setPaginationData] = useState({
    totalOffers: 0,
    current_page: 1,
    pageSize: 12,
    pages_count: 1,
  });
  const [filters, setFilters] = useState({
    page: 1,
    roomType: [],
    group: [],
    month: [],
    levels: [],
    cancel: [],
    sport: [],
    sub_sport: [],
    continent: [],
    country: [],
    state: [],
    from: [],
    to: [],
    environments: [],
    all_sports: false,
    all_destinations: false,
  });
  const [canonicalUrl, setCanonicalUrl] = useState("");

  const router = useRouter();
  const { query } = router;
  const { t, i18n } = useTranslation("common");
  const isEn = i18n.language === "en";
  const { width } = useWindowSize();
  const isSmallLaptop = width <= 1300 && width >= 999;
  const isMobile = width <= 768;

  useEffect(() => {
    // count active filters
    const selectedFilters = Object.entries(filters).reduce(
      (acc, [key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          return [
            ...acc,
            ...value.map((val) => ({
              type: key,
              value: val,
            })),
          ];
        } else if (value === 1 || value === true) {
          return [...acc, { type: key, value: key }];
        } else if (value && typeof value === "string") {
          return [...acc, { type: key, value }];
        }
        return acc;
      },
      []
    );

    let count = 0;
    selectedFilters.forEach((fltr) => {
      if (
        fltr.type === "page" ||
        fltr.type === "sport" ||
        fltr.type === "to" ||
        fltr.type === "from" ||
        fltr.type === "continent" ||
        fltr.type === "country" ||
        fltr.type === "state" ||
        fltr.type === "month" ||
        fltr.type === "all_sports" ||
        fltr.type === "all_destinations"
      ) {
        return;
      }

      count += 1;
    });

    setActiveFiltersCount(count);
  }, [filters]);

  const handlePageChange = (page) => {
    setPaginationData((prevPaginationData) => ({
      ...prevPaginationData,
      current_page: page,
    }));
    setFilters((prevFilterData) => ({
      ...prevFilterData,
      page: page,
    }));

    const newQuery = { ...router.query, page: page.toString() };
    if (page === 1) {
      delete newQuery.page;
    }

    router.push(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true }
    );

    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePostParams = (filters) => {
    const queryParams = new URLSearchParams();
    queryParams.append("page", filters.page);

    Object.entries(filters).forEach(([key, value]) => {
      const cleanedKey = key.replace(/\[\]$/, "");
      if (cleanedKey === "page" || !value) return;
      if (Array.isArray(value) && value.length > 0) {
        queryParams.append(cleanedKey, value.join(","));
      } else if (value === true || value === false) {
        queryParams.append(cleanedKey, value.toString());
      } else if (value && typeof value === "string") {
        queryParams.append(cleanedKey, value);
      }
    });

    return queryParams.toString();
  };

  const getFilters = async (params) => {
    try {
      setIsFiltersLoading(true);
      setHasFilterReqFailed(false);
      const res = await request.get(`search/v2/filters?${params}`);
      setFiltersData(res?.data?.filters || {});
    } catch (err) {
      setHasFilterReqFailed(true);
      console.log(err.response?.data || err.message);
    } finally {
      setIsFiltersLoading(false);
      setLoadSearchBar(true);
    }
  };

  const getOffers = async (filters) => {
    try {
      setHasFailed(false);
      setIsLoading(true);
      setIsFiltersLoadingInline(true);
      const queryParams = handlePostParams(filters);

      const res = await request.get(`search/v2?${queryParams}`);
      await getFilters(queryParams);

      const data = res?.data;
      setOffers(data?.search_data?.items || []);
      setMainTitle(data?.search_data?.title || null);
      setPaginationData((prevPaginationData) => ({
        ...prevPaginationData,
        totalOffers: data?.search_data?.total_items || 0,
        current_page: data?.search_data?.current_page || 1,
        pageSize:
          data?.search_data?.items?.length || prevPaginationData.pageSize,
        pages_count: data?.search_data?.pages_count || 1,
      }));
    } catch (err) {
      setHasFailed(true);
      console.log(err.response?.data || err.message);
    } finally {
      setIsLoading(false);
      setIsFiltersLoadingInline(false);
    }
  };

  useEffect(() => {
    const { pathname } = router;
    const filteredQuery = { ...query };
    if (filteredQuery.page && filteredQuery.page !== "1") {
      delete filteredQuery.page;
    }

    const searchParams = Object.keys(filteredQuery)
      .map((key) => {
        const value = filteredQuery[key];
        return Array.isArray(value)
          ? value.map((v) => `${key}=${encodeURIComponent(v)}`).join("&")
          : `${key}=${encodeURIComponent(value)}`;
      })
      .join("&");

    const baseUrl = process.env.NEXT_PUBLIC_SITE_ADDRESS.replace(/\/$/, "");
    const cleanPathname = pathname.replace(/^\//, "");

    const finalURL = `${baseUrl}/${cleanPathname}${
      searchParams ? `?${searchParams}` : ""
    }`;

    setCanonicalUrl(finalURL);

    const initialFilters = {
      page: 1,
      roomType: [],
      group: [],
      month: [],
      levels: [],
      cancel: [],
      sport: [],
      sub_sport: [],
      continent: [],
      country: [],
      state: [],
      from: [],
      to: [],
      environments: [],
      all_sports: false,
      all_destinations: false,
    };
    const sportsAliases = [
      "surf",
      "yoga",
      "fitness",
      "hiking",
      "klettern-bouldern-2",
      "bunter-aktivmix",
      "martial-arts",
      "kajak-kanu",
      "fahrrad-mountainbike",
      "kitesurfen",
      "winter-sports",
    ];

    Object.entries(query).forEach(([key, value]) => {
      const cleanedKey = key.replace(/\[\]$/, "");

      if (cleanedKey === "page") {
        initialFilters.page = parseInt(value, 10) || 1;
      } else if (cleanedKey === "sport") {
        if (Array.isArray(value)) {
          value.forEach((val) => {
            if (sportsAliases.includes(val)) {
              initialFilters.sport.push(val);
            } else {
              initialFilters.sub_sport.push(val);
            }
          });
        } else if (value.includes(",")) {
          value.split(",").forEach((val) => {
            if (sportsAliases.includes(val)) {
              initialFilters.sport.push(val);
            } else {
              initialFilters.sub_sport.push(val);
            }
          });
        } else {
          if (sportsAliases.includes(value)) {
            initialFilters.sport.push(value);
          } else {
            initialFilters.sub_sport.push(value);
          }
        }
      } else if (Array.isArray(value)) {
        initialFilters[cleanedKey] = value;
      } else if (value.includes(",")) {
        initialFilters[cleanedKey] = value.split(",");
      } else {
        initialFilters[cleanedKey] = [value];
      }
    });

    setFilters(initialFilters);
    getOffers(initialFilters);
  }, [query]);

  const handleResetAllFilters = () => {
    const preservedFilters = {
      page: filters.page || 1,
      month: filters.month || [],
      sport: filters.sport || [],
      continent: filters.continent || [],
      country: filters.country || [],
      state: filters.state || [],
      from: filters.from || [],
      to: filters.to || [],
    };

    // Reset
    setFilters((prevFilterData) => ({
      ...prevFilterData,
      roomType: [],
      group: [],
      levels: [],
      cancel: [],
      sub_sport: [],
      environments: [],
      all_sports: false,
      all_destinations: false,
    }));

    const query = {
      ...preservedFilters,
      page: preservedFilters.page,
    };

    router.push(
      {
        pathname: router.pathname,
        query: query,
      },
      undefined,
      { shallow: true }
    );
  };

  const searchURL = `${
    process.env.NEXT_PUBLIC_SITE_ADDRESS
  }${router?.asPath?.substring(1)}`;

  return (
    <>
      <NextSeo
        title={t("search.title_page")}
        description={t("meta.search_desc")}
        canonical={canonicalUrl || ""}
        openGraph={{
          url: canonicalUrl || "",
          title: t("search.title_page"),
          description: t("meta.search_desc"),
          site_name: "moverii.de",
        }}
        twitter={{
          handle: "@handle",
          site: "@site",
          cardType: "summary_large_image",
        }}
      />
      <FavoriteOffersLoader />

      <SearchBar
        filters={filters}
        filterData={filtersData}
        mainTitle={mainTitle}
        isLoading={isLoading}
        setOpenFilter={setOpenFilter}
        setOpenMap={setOpenMap}
        totalOffers={paginationData.totalOffers}
        loadSearchBar={loadSearchBar}
        activeFiltersCount={activeFiltersCount}
      />

      <section
        className={`md:pb-[30px] !container mx-auto pt-5 ${
          isSmallLaptop && "!px-5"
        }`}
      >
        {/* header */}
        <div className="w-full hidden md:flex items-center justify-between gap-3 z-[100] md:z-10 bg-white py-2 md:py-0 lg:-mt-5">
          <div className="flex justify-start items-center gap-3">
            <div className="hidden md:flex">
              {isLoading ? (
                <Skeleton.Button
                  active
                  className="!h-[35px] !w-[200px] md:!w-[400px] xl:!w-[500px]"
                />
              ) : (
                <h1 className="lg:text-[32px] md:text-[26px] text-[24px] font-bold">
                  {isEn
                    ? mainTitle?.en ?? "Sport & active journeys"
                    : mainTitle?.de ?? "Sport- & Aktivreisen"}
                </h1>
              )}
            </div>
            {isLoading ? (
              <Skeleton.Button
                active
                className="!h-[20px] !w-[80px] md:!-mb-4"
              />
            ) : (
              <p className="text-base mt-2 whitespace-nowrap">
                (
                {paginationData.totalOffers <= 1
                  ? `${paginationData.totalOffers} ${t("search.result")}`
                  : `${paginationData.totalOffers} ${t("search.results")}`}
                )
              </p>
            )}
          </div>

          <div className="flex items-center justify-between gap-5 xl:hidden mb-5">
            <div
              className={`px-5 py-3 bg-[#e3f9fc] rounded-full flex items-center gap-2 cursor-pointer relative`}
              onClick={() => setOpenMap(true)}
            >
              <div>
                <IoMapOutline className="text-xl" />
              </div>
              <p className="text-base font-medium whitespace-nowrap">
                {t("offer.mapView")}
              </p>
            </div>
            <div
              className={`px-5 py-3 bg-[#e3f9fc] rounded-full flex items-center gap-2 cursor-pointer relative ${
                activeFiltersCount > 0 ? "!border !border-green" : ""
              }`}
              onClick={() => setOpenFilter(true)}
            >
              {activeFiltersCount > 0 && (
                <span className="absolute px-[7px] py-[0.5px] rounded-full bg-green text-white text-[12px] -top-[7px] right-0 font-medium">
                  {activeFiltersCount}
                </span>
              )}
              <RxMixerHorizontal className="text-[22px]" />
              <p className="text-base font-medium whitespace-nowrap">
                {t("offer.open_drawer_filter")}
              </p>
            </div>
          </div>
        </div>

        <div className="w-full grid grid-cols-1 xl:grid-cols-8 gap-4 px-5 md:px-0 mt-5">
          <div className="col-span-1 xl:col-span-2 hidden xl:flex transition-all duration-500">
            <FilterBoxes
              isFiltersLoading={isFiltersLoading}
              filtersData={filtersData}
              setFilters={setFilters}
              filters={filters}
              isFiltersLoadingInline={isFiltersLoadingInline}
              router={router}
              filterData={filtersData}
              hasFilterReqFailed={hasFilterReqFailed}
              setOpenMap={setOpenMap}
              openMap={openMap}
              getFiltersData={() => {
                const params = handlePostParams(filters);
                getFilters(params);
              }}
            />
          </div>
          <div className="col-span-1 xl:col-span-6 transition-all duration-500">
            {!isLoading ? (
              offers?.length > 0 ? (
                <List
                  className="search_pagination"
                  bordered={false}
                  pagination={{
                    onChange: (page) => {
                      handlePageChange(page);
                    },
                    showSizeChanger: false,
                    hideOnSinglePage: true,
                    pageSize: paginationData.pageSize,
                    total: paginationData.pages_count * paginationData.pageSize,
                    current: paginationData.current_page,
                    defaultCurrent: 1,
                    defaultPageSize: paginationData.pageSize,
                    pageSizeOptions: [paginationData.pageSize],
                  }}
                  rowKey="id"
                  dataSource={offers}
                  renderItem={(item, index) => (
                    <List.Item
                      key={item?.uuid + index}
                      className="pb-[24px] flex flex-col"
                    >
                      <OfferItem
                        item={item}
                        index={index + 1}
                        router={router}
                        isMobile={isMobile}
                      />

                      <Adds
                        index={index + 1}
                        page={parseInt(paginationData.current_page)}
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <div className="w-full py-10">
                  <motion.div
                    initial={{ opacity: 0.5, scale: 0.5 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{
                      duration: 0.1,
                      type: "spring",
                      stiffness: 300,
                      damping: 50,
                    }}
                  >
                    <Empty description={t("search.offer_is_empty")} />
                  </motion.div>
                </div>
              )
            ) : (
              <div className="w-full flex flex-col gap-5">
                {Array.from({ length: 14 }, (_, i) => (
                  <div key={"offer-placeholder-" + i} className="w-full">
                    <Skeleton.Button
                      active
                      className="!h-[600px] md:!h-[255px] !w-full !rounded-2xl !pb-[24px]"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col py-[50px]">
          <Newsletter
            title={t("contact.newsletter_title")}
            description={t("contact.newsletter_description")}
            inputPlaceholder={t("contact.newsletter_placeholder")}
            buttonTitle={t("contact.newsletter_button")}
          />
          <HomeSectioncomminity />
        </div>

        {hasFailed && (
          <Modal
            footer={false}
            open={hasFailed}
            centered
            destroyOnClose
            onCancel={() => {
              setHasFailed(false);
            }}
          >
            <div className="w-full flex flex-col items-center justify-center py-8 px-4">
              <div className="flex flex-col items-center text-center">
                <p className="font-bold text-lg text-red">
                  {t("global.offer_page_error_title")}
                </p>
                <p className="py-4 text-base">
                  {t("global.offer_page_error_desc")}
                </p>
                <Button
                  className="btn-orange px-10 mt-5 text-base font-medium !bg-orange !text-white"
                  onClick={() => {
                    getOffers(filters);
                    setHasFailed(false);
                  }}
                  aria-label={t("global.offer_page_error_btn")}
                >
                  {t("global.offer_page_error_btn")}
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {openFilter && (
          <Drawer
            open={openFilter}
            closable={false}
            placement={openMap ? "left" : "right"}
            width={isMobile ? "100vw" : "400px"}
            destroyOnClose
            className="[&_.ant-drawer-header]:!px-0 [&_.ant-drawer-header-title]:!px-0 [&_.ant-drawer-body]:!px-0 [&_.ant-drawer-footer]:!px-0"
            title={
              <div className="w-full h-full flex items-center justify-between container mx-auto px-4">
                <p className="text-[22px] font-semibold">
                  {t("search.filters")}
                </p>
                <span
                  onClick={() => setOpenFilter(false)}
                  className="cursor-pointer"
                >
                  <IoClose className="text-2xl" />
                </span>
              </div>
            }
            footer={
              <div className="py-2 flex justify-between container mx-auto px-4">
                <button
                  onClick={handleResetAllFilters}
                  className="bg-white text-black font-bold text-sm sm:text-base py-3 px-3 w-[30%]"
                  aria-label={t("search.delete_all_filters")}
                >
                  {t("search.delete_all_filters")}
                </button>
                <button
                  onClick={() => setOpenFilter(false)}
                  className="bg-orange text-white font-bold text-sm sm:text-base py-3 rounded-3xl w-[60%]"
                  aria-label={`Show ${paginationData.totalOffers} results`}
                >
                  {t("search.mobile_filter_show")}{" "}
                  {paginationData.totalOffers <= 1
                    ? `${paginationData.totalOffers} ${t("search.result")}`
                    : `${paginationData.totalOffers} ${t(
                        "search.results"
                      )}`}{" "}
                </button>
              </div>
            }
          >
            <div className="container mx-auto px-4">
              <FilterBoxes
                isFiltersLoading={isFiltersLoading}
                filtersData={filtersData}
                setFilters={setFilters}
                filters={filters}
                isFiltersLoadingInline={isFiltersLoadingInline}
                router={router}
                filterData={filtersData}
                hasFilterReqFailed={hasFilterReqFailed}
                setOpenMap={setOpenMap}
                openMap={openMap}
                getFiltersData={() => {
                  const params = handlePostParams(filters);
                  getFilters(params);
                }}
              />
            </div>
          </Drawer>
        )}

        {openMap && (
          <GoogleMapModal
            open={openMap}
            setOpen={setOpenMap}
            offers={offers}
            countryFilter={
              filters?.continent ||
              filters?.country ||
              filters?.state ||
              "europe"
            }
            setOpenFilter={setOpenFilter}
            isFiltersLoadingInline={isFiltersLoadingInline}
            filters={filters}
          />
        )}
      </section>
    </>
  );
};

export default NewSearchSystem;
